import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
// FIXED: Import shared type instead of defining a duplicate local one
import { RecallItem } from "../types";

type ColorTuple = [number, number, number];

interface Theme {
  headerBg: ColorTuple;
  headerTextPrimary: ColorTuple;
  headerTextSecondary: ColorTuple;
  tableHeadBg: ColorTuple;
  categoryBg: ColorTuple;
  categoryText: ColorTuple;
  borders: ColorTuple;
  cueText: ColorTuple;
}

// --- MATH & TEXT CLEANER ---
const cleanTextForPdf = (text: string | undefined): string => {
  if (!text) return "";
  let t = text;
  // Convert Latex Fractions
  while (t.includes("\\frac{")) {
    t = t.replace(/\\frac\{([^{}]+)\}\{([^{}]+)\}/g, "($1 / $2)");
    if (!t.includes("\\frac{")) break;
  }
  // Convert Symbols
  t = t
    .replace(/\\times/g, "×")
    .replace(/\\div/g, "÷")
    .replace(/\\approx/g, "≈")
    .replace(/\\le/g, "≤")
    .replace(/\\ge/g, "≥")
    .replace(/\\neq/g, "≠")
    .replace(/\\%/g, "%")
    .replace(/\^/g, "^");
  // Clean Latex Formatting
  t = t
    .replace(/\\textbf/g, "")
    .replace(/\\textit/g, "")
    .replace(/\\text/g, "")
    .replace(/\\$/g, "")
    .replace(/\$/g, "")
    .replace(/\{/g, "")
    .replace(/\}/g, "")
    .replace(/\\/g, " ");

  // HTML to Plain Text
  const tmp = document.createElement("DIV");
  let cleaned = t.replace(/<br\s*\/?>/gi, "\n").replace(/<\/p>/gi, "\n\n");
  tmp.innerHTML = cleaned;
  return (tmp.textContent || tmp.innerText || "").trim();
};

// --- DYNAMIC STYLES ---
const getLayoutConfig = (length: "1_PAGE" | "2_PAGES" | "3_PAGES") => {
  switch (length) {
    case "1_PAGE":
      return { fontSize: 8, cellPadding: 2 };
    case "2_PAGES":
      return { fontSize: 9, cellPadding: 3 };
    case "3_PAGES":
    default:
      return { fontSize: 10, cellPadding: 5 };
  }
};

export const exportRecallSheetToPDF = (
  items: RecallItem[],
  subject: string,
  fileName: string,
  length: "1_PAGE" | "2_PAGES" | "3_PAGES"
) => {
  const doc = new jsPDF();

  // --- UPDATED: Site URL is now Google ---
  const siteUrl = "https://www.google.com/";

  const layout = getLayoutConfig(length);

  // --- FIXED: VALID PLACEHOLDER LOGO (1x1 Pixel Blue Dot) ---
  const logoBase64 =
    "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPj/HwADBwL+bXgk/gAAAABJRU5ErkJggg==";

  const theme: Theme = {
    headerBg: [224, 231, 255],
    headerTextPrimary: [49, 46, 129],
    headerTextSecondary: [79, 70, 229],
    tableHeadBg: [99, 102, 241],
    categoryBg: [199, 210, 254],
    categoryText: [55, 48, 163],
    borders: [224, 231, 255],
    cueText: [49, 46, 129],
  };

  // --- HEADER ---
  doc.setFillColor(...theme.headerBg);
  doc.rect(0, 0, 210, 45, "F");

  // 1. ADD CLICKABLE LOGO (With Crash Protection)
  try {
    doc.addImage(logoBase64, "PNG", 14, 10, 18, 18);
    doc.link(14, 10, 18, 18, { url: siteUrl });
  } catch (e) {
    console.warn("Logo failed to load, skipping...", e);
    doc.setFillColor(...theme.headerTextSecondary);
    doc.rect(14, 10, 18, 18, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(8);
    doc.text("FG", 16, 21);
  }

  // 2. Main Title
  doc.setFontSize(22);
  doc.setTextColor(...theme.headerTextPrimary);
  doc.text(`${subject} - Active Recall Sheet`, 40, 20);

  // 3. Subtext & Date
  doc.setFontSize(10);
  doc.setTextColor(...theme.headerTextSecondary);
  doc.setFont("helvetica", "normal");
  doc.text(
    `Generated by ScoreSmart • ${new Date().toLocaleDateString()}`,
    40,
    28
  );

  // --- DATA PREPARATION ---
  const grouped: Record<string, RecallItem[]> = {};
  items.forEach((item) => {
    // FIXED: Safely check for category, defaulting to "General" if missing
    // We cast to 'any' here just to check for 'category' property if it's missing in types.ts
    const cat = (item as any).category || "General";
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(item);
  });

  let bodyData: any[] = [];
  Object.keys(grouped).forEach((cat) => {
    // Add Category Header Row
    bodyData.push([
      {
        content: cat.toUpperCase(),
        colSpan: 2,
        styles: {
          fillColor: theme.categoryBg,
          textColor: theme.categoryText,
          fontStyle: "bold",
          halign: "left",
          cellPadding: {
            top: layout.cellPadding + 1,
            bottom: layout.cellPadding + 1,
            left: 4,
          },
        },
      },
    ]);
    // Add Items
    grouped[cat].forEach((item) => {
      // FIXED: Support both 'cue/recall' AND 'question/answer' property names
      const qText = item.cue || item.question || "";
      const aText = item.recall || item.answer || "";

      bodyData.push([cleanTextForPdf(qText), cleanTextForPdf(aText)]);
    });
  });

  // --- RENDER TABLE ---
  autoTable(doc, {
    startY: 45,
    head: [["KEY TERM / TOPIC", "DETAILS & MECHANISM (Cover to Recall)"]],
    body: bodyData,
    theme: "grid",
    styles: {
      fontSize: layout.fontSize,
      cellPadding: layout.cellPadding,
      lineColor: theme.borders,
      lineWidth: 0.1,
      textColor: [51, 65, 85],
      overflow: "linebreak",
      valign: "middle",
    },
    headStyles: {
      fillColor: theme.tableHeadBg,
      textColor: [255, 255, 255],
      fontSize: layout.fontSize + 1,
      fontStyle: "bold",
      halign: "left",
    },
    columnStyles: {
      0: { cellWidth: 35, fontStyle: "bold", textColor: theme.cueText },
      1: { cellWidth: "auto" },
    },
    alternateRowStyles: {
      fillColor: [250, 250, 252],
    },
  });

  doc.save(`${fileName}_RecallSheet.pdf`);
};